/**
 * TemplateFormDialog Component
 *
 * Dialog for creating and editing inspection templates.
 */

import { useState, useEffect } from 'react';
import {
  DialogContent,
  Box,
  Divider,
  TextField,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Autocomplete,
  Alert,
  Typography,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import SaveIcon from '@mui/icons-material/Save';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  useCreateTemplate,
  useUpdateTemplate,
  useInspectionTemplates,
  type InspectionTemplate,
  type Category,
} from '@/hooks/useInspections';
import { useVehicleMakesModels } from '@/hooks/useChecklists';
import { useNotify } from '@/stores/notificationStore';
import { useServices } from '@/hooks/useServices';
import { CategoryEditor } from './CategoryEditor';
import { useTranslation } from 'react-i18next';

interface TemplateFormDialogProps {
  open: boolean;
  onClose: () => void;
  template?: InspectionTemplate | null;
}

type TabValue = 'general' | 'categories';

// Default categories for new templates (Finnish vehicle inspection standard)
const defaultCategories: Category[] = [
  {
    id: 'cat-exterior',
    name: 'Ulkopuoli',
    description: 'Korin ja ulkopintojen tarkastus',
    sortOrder: 0,
    items: [
      { id: 'ext-1', name: 'Tuulilasi', description: 'Halkeamat, kiveniskemät', sortOrder: 0 },
      { id: 'ext-2', name: 'Valot edessä', description: 'Ajovalot, huomiovalot, vilkut', sortOrder: 1 },
      { id: 'ext-3', name: 'Valot takana', description: 'Takavalot, peruutusvalot, vilkut', sortOrder: 2 },
      { id: 'ext-4', name: 'Peilit', description: 'Sivupeilit, taustapeili', sortOrder: 3 },
      { id: 'ext-5', name: 'Pyyhkijät', description: 'Sulat ja toiminta', sortOrder: 4 },
    ],
  },
  {
    id: 'cat-interior',
    name: 'Sisätilat',
    description: 'Sisätilojen ja hallintalaitteiden tarkastus',
    sortOrder: 1,
    items: [
      { id: 'int-1', name: 'Turvavyöt', description: 'Kiinnitys ja lukitus', sortOrder: 0 },
      { id: 'int-2', name: 'Mittaristo', description: 'Varoitusvalot ja näytöt', sortOrder: 1 },
      { id: 'int-3', name: 'Ilmastointi', description: 'Lämmitys ja jäähdytys', sortOrder: 2 },
      { id: 'int-4', name: 'Äänimerkinantolaite', description: 'Torvi', sortOrder: 3 },
    ],
  },
  {
    id: 'cat-underhood',
    name: 'Moottoritila',
    description: 'Moottorin ja nesteiden tarkastus',
    sortOrder: 2,
    items: [
      { id: 'eng-1', name: 'Moottoriöljy', description: 'Taso ja kunto', sortOrder: 0, hasMeasurement: true, measurementUnit: 'taso' },
      { id: 'eng-2', name: 'Jäähdytysneste', description: 'Taso ja jäätymispiste', sortOrder: 1 },
      { id: 'eng-3', name: 'Jarruneste', description: 'Taso', sortOrder: 2 },
      { id: 'eng-4', name: 'Hihnat', description: 'Kuluma ja kireys', sortOrder: 3 },
      { id: 'eng-5', name: 'Akku', description: 'Kunto ja kiinnitys', sortOrder: 4 },
    ],
  },
  {
    id: 'cat-brakes',
    name: 'Jarrut',
    description: 'Jarrujärjestelmän tarkastus',
    sortOrder: 3,
    items: [
      { id: 'brk-1', name: 'Etujarrupalat', description: 'Paksuus', sortOrder: 0, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'brk-2', name: 'Takajarrupalat', description: 'Paksuus', sortOrder: 1, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'brk-3', name: 'Jarrulevyt edessä', description: 'Paksuus ja kunto', sortOrder: 2, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'brk-4', name: 'Jarrulevyt takana', description: 'Paksuus ja kunto', sortOrder: 3, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'brk-5', name: 'Käsijarru', description: 'Toiminta', sortOrder: 4 },
    ],
  },
  {
    id: 'cat-tires',
    name: 'Renkaat',
    description: 'Renkaiden ja vanteiden tarkastus',
    sortOrder: 4,
    items: [
      { id: 'tir-1', name: 'Vasen eturengas', description: 'Urasyvyys ja kunto', sortOrder: 0, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'tir-2', name: 'Oikea eturengas', description: 'Urasyvyys ja kunto', sortOrder: 1, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'tir-3', name: 'Vasen takarengas', description: 'Urasyvyys ja kunto', sortOrder: 2, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'tir-4', name: 'Oikea takarengas', description: 'Urasyvyys ja kunto', sortOrder: 3, hasMeasurement: true, measurementUnit: 'mm' },
      { id: 'tir-5', name: 'Rengaspaineet', description: 'Paineiden tarkistus', sortOrder: 4, hasMeasurement: true, measurementUnit: 'bar' },
    ],
  },
  {
    id: 'cat-undercar',
    name: 'Alusta',
    description: 'Alustan ja jousituksen tarkastus',
    sortOrder: 5,
    items: [
      { id: 'und-1', name: 'Iskunvaimentimet', description: 'Vuodot ja toiminta', sortOrder: 0 },
      { id: 'und-2', name: 'Jouset', description: 'Kunto', sortOrder: 1 },
      { id: 'und-3', name: 'Ohjaus', description: 'Välykset', sortOrder: 2 },
      { id: 'und-4', name: 'Vetoakselit', description: 'Suojakumit', sortOrder: 3 },
      { id: 'und-5', name: 'Pakoputkisto', description: 'Vuodot ja kiinnitys', sortOrder: 4 },
    ],
  },
];

// Vehicle type options
const vehicleTypeOptions = [
  'Henkilöauto',
  'Pakettiauto',
  'Kuorma-auto',
  'Moottoripyörä',
  'Perävaunu',
  'Sähköauto',
  'Hybridi',
];


export function TemplateFormDialog({ open, onClose, template }: TemplateFormDialogProps) {
  const notify = useNotify();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const { data: allServices = [] } = useServices({ isActive: true });
  const { data: allTemplates } = useInspectionTemplates();
  const { data: vehicleMakesModels } = useVehicleMakesModels();
  const serviceTypeOptions = allServices.map((s) => s.name);

  const [activeTab, setActiveTab] = useState<TabValue>('general');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isBase, setIsBase] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState<string[]>([]);
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);
  const [selectedMakes, setSelectedMakes] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [sourceTemplateId, setSourceTemplateId] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const isEditing = !!template;
  const { t } = useTranslation('settings');
  const isLoading = createTemplate.isPending || updateTemplate.isPending;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (template) {
        setName(template.name);
        setDescription(template.description ?? '');
        setIsDefault(template.isDefault);
        setIsBase(template.isBase ?? false);
        setVehicleTypes(template.vehicleTypes ?? []);
        setServiceTypes(template.serviceTypes ?? []);
        setSelectedMakes(template.vehicleMakes ?? []);
        setSelectedModels(template.vehicleModels ?? []);
        setSourceTemplateId(template.sourceTemplateId ?? null);
        setCategories(template.categories);
      } else {
        setName('');
        setDescription('');
        setIsDefault(false);
        setIsBase(false);
        setVehicleTypes([]);
        setServiceTypes([]);
        setSelectedMakes([]);
        setSelectedModels([]);
        setSourceTemplateId(null);
        setCategories(defaultCategories);
      }
      setActiveTab('general');
      setError(null);
      setIsDirty(false);
    }
  }, [open, template]);

  const handleSave = async () => {
    setError(null);

    if (!name.trim()) {
      setError(t('inspectionTemplates.nameRequired'));
      return;
    }

    if (categories.length === 0) {
      setError(t('inspectionTemplates.needCategory'));
      return;
    }

    // Check that all categories have at least one item
    const emptyCategory = categories.find((c) => c.items.length === 0);
    if (emptyCategory) {
      setError(t('inspectionTemplates.categoryEmpty', { name: emptyCategory.name }));
      setActiveTab('categories');
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        isDefault,
        isBase,
        vehicleTypes: vehicleTypes.length > 0 ? vehicleTypes : undefined,
        serviceTypes: serviceTypes.length > 0 ? serviceTypes : undefined,
        vehicleMakes: selectedMakes.length > 0 ? selectedMakes : null,
        vehicleModels: selectedModels.length > 0 ? selectedModels : null,
        sourceTemplateId: sourceTemplateId || null,
        categories,
      };

      if (isEditing) {
        await updateTemplate.mutateAsync({ id: template.id, ...payload });
        notify.success(t('inspectionTemplates.templateUpdated'), t('inspectionTemplates.templateUpdatedDesc'));
      } else {
        await createTemplate.mutateAsync(payload);
        notify.success(t('inspectionTemplates.templateCreated'), t('inspectionTemplates.templateCreatedDesc'));
      }
      onClose();
    } catch {
      setError(t('inspectionTemplates.saveFailed'));
    }
  };

  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('inspectionTemplates.editFormTitle') : t('inspectionTemplates.newFormTitle')}
      isDirty={isDirty}
      onSubmit={() => void handleSave()}
      isLoading={isLoading}
      submitLabel={isEditing ? t('inspectionTemplates.saveChanges') : t('inspectionTemplates.createTemplate')}
      submitIcon={<SaveIcon />}
      maxWidth="md"
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={activeTab} onChange={(_e, v) => { setActiveTab(v); }}>
          <Tab
            value="general"
            icon={<SettingsIcon />}
            iconPosition="start"
            label={t('inspectionTemplates.basicInfoTab')}
          />
          <Tab
            value="categories"
            icon={<CategoryIcon />}
            iconPosition="start"
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('inspectionTemplates.categoriesTab')}
                <Chip size="small" label={`${categories.length} / ${totalItems}`} />
              </Box>
            }
          />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); }}>
            {error}
          </Alert>
        )}

        {activeTab === 'general' && (
          <Stack spacing={2}>
            {/* Clone from existing — only when creating */}
            {!isEditing && (allTemplates?.data?.length ?? 0) > 0 && (
              <>
                <Autocomplete
                  size="small"
                  options={allTemplates?.data ?? []}
                  getOptionLabel={(option: InspectionTemplate) => option.name}
                  value={allTemplates?.data?.find((t2: InspectionTemplate) => t2.id === sourceTemplateId) ?? null}
                  onChange={(_e, newValue) => {
                    if (newValue) {
                      setSourceTemplateId(newValue.id);
                      setCategories(newValue.categories);
                      if (!name) setName(newValue.name);
                      if (!description) setDescription(newValue.description ?? '');
                      setVehicleTypes(newValue.vehicleTypes ?? []);
                      setServiceTypes(newValue.serviceTypes ?? []);
                      setSelectedMakes(newValue.vehicleMakes ?? []);
                      setSelectedModels(newValue.vehicleModels ?? []);
                      setIsBase(newValue.isBase ?? false);
                      setIsDirty(true);
                    } else {
                      setSourceTemplateId(null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('inspectionTemplates.cloneFrom')}
                      helperText={t('inspectionTemplates.cloneFromHint')}
                    />
                  )}
                />
                <Divider />
              </>
            )}

            <TextField
              label={t('inspectionTemplates.templateName')}
              value={name}
              onChange={(e) => { setName(e.target.value); setIsDirty(true); }}
              fullWidth
              required
              size="small"
              placeholder={t('inspectionTemplates.templateNamePlaceholder')}
            />

            <TextField
              label={t('inspectionTemplates.descriptionLabel')}
              value={description}
              onChange={(e) => { setDescription(e.target.value); setIsDirty(true); }}
              fullWidth
              multiline
              rows={2}
              placeholder={t('inspectionTemplates.descriptionPlaceholder')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => { setIsDefault(e.target.checked); setIsDirty(true); }}
                />
              }
              label={t('inspectionTemplates.setAsDefault')}
            />

            <Autocomplete
              multiple
              options={vehicleTypeOptions}
              value={vehicleTypes}
              onChange={(_, value) => { setVehicleTypes(value); setIsDirty(true); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('inspectionTemplates.vehicleTypes')}
                  placeholder={t('inspectionTemplates.vehicleTypesPlaceholder')}
                  helperText={t('inspectionTemplates.vehicleTypesHint')}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
            />

            <Autocomplete
              multiple
              size="small"
              options={serviceTypeOptions}
              value={serviceTypes}
              onChange={(_, value) => { setServiceTypes(value); setIsDirty(true); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('inspectionTemplates.serviceTypes')}
                  placeholder={t('inspectionTemplates.serviceTypesPlaceholder')}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                ))
              }
            />

            <Divider />

            {/* Base template + Make/Model linking */}
            <Typography variant="subtitle2" color="text.secondary">
              {t('inspectionTemplates.linkingTitle')}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isBase}
                  onChange={(e) => { setIsBase(e.target.checked); setIsDirty(true); }}
                />
              }
              label={<Typography variant="body2">{t('inspectionTemplates.isBase')}</Typography>}
            />
            {isBase && (
              <Alert severity="info" variant="outlined" sx={{ mt: -1, py: 0 }}>
                <Typography variant="caption">{t('inspectionTemplates.isBaseHint')}</Typography>
              </Alert>
            )}

            <Box display="flex" gap={2}>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={vehicleMakesModels?.makes ?? []}
                value={selectedMakes}
                onChange={(_e, newValue) => { setSelectedMakes(newValue); setIsDirty(true); }}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label={t('inspectionTemplates.vehicleMakes')} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
              />
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={vehicleMakesModels?.models ?? []}
                value={selectedModels}
                onChange={(_e, newValue) => { setSelectedModels(newValue); setIsDirty(true); }}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label={t('inspectionTemplates.vehicleModels')} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
              />
            </Box>
          </Stack>
        )}

        {activeTab === 'categories' && (
          <CategoryEditor categories={categories} onChange={(cats) => { setCategories(cats); setIsDirty(true); }} />
        )}
      </DialogContent>
    </FormDialog>
  );
}
